/**
 * Paper tint registry + apply/load helpers.
 *
 * The .paper utility paints its bokashi color washes by reading five
 * --bokashi-* custom properties from the cascade. Each tint is a CSS
 * modifier class (.paper--<id>) that rebinds those tokens locally;
 * applied to <html>, the cascade tints every .paper descendant.
 *
 * The chosen tint persists in localStorage under TINT_STORAGE_KEY so
 * the user's pick survives reloads. SettingsScreen exposes the picker
 * UI; TintsLab uses the same list for its comparison grid.
 */

export interface PaperTint {
  /** Modifier slug — empty string means the :root default (Aged). */
  id: string
  name: string
  subtitle: string
  /** Conceptual category — paintings REPLACE the whole .paper
   *  background with a full-bleed image; bokashi options rebind the
   *  color tokens of the existing kozo+bokashi stack. SettingsScreen
   *  renders one section per kind so users see the choice as two
   *  distinct flavors. Only one selection is active at a time. */
  kind: 'painting' | 'bokashi'
}

export const TINTS: ReadonlyArray<PaperTint> = [
  // Paintings — full-bleed scene replaces the kozo/bokashi paper stack.
  { id: 'japanese', name: 'Japanese Tableau', kind: 'painting', subtitle: 'painted shop interior · the new default' },

  // Bokashi color stories — keep the kozo washi photo + fold-memory
  // grid + sakura corner, rebind the five dye-pool colors.
  { id: 'aged',    kind: 'bokashi', name: 'Aged Manuscript', subtitle: 'tea-stained · tan + sepia + khaki + dust' },
  { id: 'sunlit',  kind: 'bokashi', name: 'Sunlit Cream',    subtitle: 'warm gold · sakura + yuzu + matcha + water' },
  { id: 'moonlit', kind: 'bokashi', name: 'Moonlit Indigo',  subtitle: 'cool dawn · periwinkle + lavender + teal-mist + indigo' },
  { id: 'plum',    kind: 'bokashi', name: 'Plum Garden',     subtitle: 'romantic warm · sakura + peach + dusty-rose + lilac' },
  { id: 'matcha',  kind: 'bokashi', name: 'Matcha Field',    subtitle: 'garden cool · chartreuse + leaf + matcha + celadon' },
  { id: 'bone',    kind: 'bokashi', name: 'Bone White',      subtitle: 'minimal ceremonial · near-monochrome paper' },
]

export type TintId = (typeof TINTS)[number]['id']
export type TintKind = (typeof TINTS)[number]['kind']

/** Split into the two picker groups SettingsScreen renders. Stable
 *  source-order within each group. */
export const PAINTING_TINTS: ReadonlyArray<PaperTint> = TINTS.filter((t) => t.kind === 'painting')
export const BOKASHI_TINTS: ReadonlyArray<PaperTint> = TINTS.filter((t) => t.kind === 'bokashi')

const TINT_STORAGE_KEY = 'origami-tint'
/** Bumped any time the DEFAULT_TINT changes. Migration in loadTint()
 *  forces every device once to the new default the first time it
 *  loads under the new version; users can then re-pick a non-default
 *  in Settings and that survives across future bumps. */
const TINT_VERSION_KEY = 'origami-tint-version'
const TINT_VERSION_CURRENT = '2'
/** New default: the painted Japanese tableau replaces the kozo bokashi
 *  stack on .paper surfaces. Other tints remain available in Paper
 *  Settings for users who prefer the original color-story aesthetic. */
const DEFAULT_TINT: TintId = 'japanese'
const TINT_IDS: ReadonlySet<string> = new Set(TINTS.map((t) => t.id))

/** Read the persisted tint, defaulting to DEFAULT_TINT. On first load
 *  under a new TINT_VERSION_CURRENT we overwrite any previously-saved
 *  tint with the new default — otherwise existing devices would stay
 *  on whatever tint was set BEFORE the default changed. Subsequent
 *  loads under the same version respect the user's explicit pick. */
export function loadTint(): TintId {
  if (typeof window === 'undefined') return DEFAULT_TINT
  const savedVersion = window.localStorage.getItem(TINT_VERSION_KEY)
  if (savedVersion !== TINT_VERSION_CURRENT) {
    window.localStorage.setItem(TINT_VERSION_KEY, TINT_VERSION_CURRENT)
    window.localStorage.setItem(TINT_STORAGE_KEY, DEFAULT_TINT)
    return DEFAULT_TINT
  }
  const saved = window.localStorage.getItem(TINT_STORAGE_KEY)
  return saved && TINT_IDS.has(saved) ? saved : DEFAULT_TINT
}

/** Persist a tint choice and apply it to <html> in one call. */
export function setTint(id: TintId): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(TINT_STORAGE_KEY, id)
  applyTintToRoot(id)
}

/** Apply a tint class to <html>, removing any prior tint class.
 *  Idempotent — calling with the current tint is a no-op. */
export function applyTintToRoot(id: TintId): void {
  if (typeof window === 'undefined') return
  const root = document.documentElement
  for (const t of TINTS) {
    if (t.id) root.classList.remove(`paper--${t.id}`)
  }
  root.classList.add(`paper--${id}`)
}
